import React, { useRef, useState, useEffect } from "react";
import "./AddDriversModal.scss";

import closeIcon from "../../../../../../Assets/images/close-icon.svg";
import generatePwdIcon from "../../../../../../Assets/images/generate-password.png";
import calendarIcon from "../../../../../../Assets/images/calender.svg";
import redIcon from "../../../../../../Assets/images/close-red.svg"

import { isValidEmail } from "../../../../../../Utils/Validations/validateEmail";
import { isValidPassword } from "../../../../../../Utils/Validations/validatePassword";
import { isValidCanadianPhone } from "../../../../../../Utils/Validations/validatePhone";
import { FIELD_MAX_LENGTHS } from "../../../Utils/Constants";
import PasswordTooltip from "../../../../../../Utils/PasswordToolTip/PasswordToolTip";
import apiClient from "../../../../../../Config/apiClient";
import Loader from "../../../../../../Context/Loader/Loader";
import { showErrorToast, showSuccessToast } from "../../../../../../Context/Toaster/Toaster";
import autoGeneratePassword from "../../../../../../Utils/AutoGeneratePassword/AutoGeneratePassword"

const rolesPath = process.env.REACT_APP_TC_ROLES_BASE_PATH;
const driversPath = process.env.REACT_APP_TC_DRIVERS_BASE_PATH;

const AddDriversModal = ({ refreshDriverList }) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    contact_no: "",
    role_id: "",
    license_no: "",
    license_expiry: "",
    license_file: null,
    email_id: "",
    password: "",
  });

  const [roles, setRoles] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const passwordRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await apiClient.get(`${rolesPath}/list`);
        if (response.data.status === "SUCCESS") {
          setRoles(response.data.data);
        }
      } catch (err) {
        showErrorToast("Failed to fetch roles.");
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (passwordRef.current && !passwordRef.current.contains(event.target)) {
        setIsPasswordFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    const newValue = files ? files[0] : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };


  const validateFields = () => {
    const newErrors = {};
    const { first_name, last_name, contact_no, email_id, password, role_id, license_no, license_expiry, license_file } = formData;

    if (!first_name.trim()) newErrors.first_name = "First name is required.";
    if (!last_name.trim()) newErrors.last_name = "Last name is required.";

    if (!contact_no.trim()) {
      newErrors.contact_no = "Contact number is required.";
    } else if (!isValidCanadianPhone(contact_no)) {
      newErrors.contact_no = "Invalid contact number.";
    }

    if (!role_id) newErrors.role_id = "Role is required.";
    if (!license_no.trim()) newErrors.license_no = "License number is required.";
    if (!license_file) newErrors.license_file = "License Document is required.";
    if (!license_expiry.trim()) newErrors.license_expiry = "License expiry is required.";

    if (!email_id.trim()) {
      newErrors.email_id = "Email is required.";
    } else if (!isValidEmail(email_id)) {
      newErrors.email_id = "Invalid email address.";
    }

    const pwdCheck = isValidPassword(password);
    if (!pwdCheck.valid) {
      newErrors.password = pwdCheck.message;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const modalEl = document.getElementById("addDriver");

    const handleModalClose = () => {
      setFormData({
        first_name: "",
        last_name: "",
        contact_no: "",
        role_id: "",
        license_no: "",
        license_expiry: "",
        license_file: null,
        email_id: "",
        password: "",
      });
      setErrors({});
      if (fileInputRef.current) fileInputRef.current.value = "";
      setShowPassword(false);
      setIsPasswordFocused(false);
    };

    if (modalEl) {
      modalEl.addEventListener("hidden.bs.modal", handleModalClose);
    }

    return () => {
      if (modalEl) {
        modalEl.removeEventListener("hidden.bs.modal", handleModalClose);
      }
    };
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    setLoading(true);
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null) payload.append(key, value);
      });

      const response = await apiClient.post(`${driversPath}/add`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.status === "SUCCESS") {
        showSuccessToast("Driver added successfully.");
        refreshDriverList()
        setFormData({
          first_name: "",
          last_name: "",
          contact_no: "",
          role_id: "",
          license_no: "",
          license_expiry: "",
          license_file: null,
          email_id: "",
          password: "",
        });
        setErrors({});
        document.getElementById("addDriverModalClose")?.click();
      } else {
        showErrorToast(response.data.message || "Failed to add driver.");
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      showErrorToast(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal right rtm-modal fade" id="addDriver" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-labelledby="addDriverLabel" aria-hidden="true">
      <div className="modal-dialog rtl-dialog">
        <div className="modal-content rtl-content animate__animated animate__fadeInRight">
          <form onSubmit={handleSubmit} noValidate>
            <div className="ajob-content">
              <div className="cpf-top cpf-border">
                <h2 id="addDriverLabel">Add Driver</h2>
                <button id="addDriverModalClose" type="button" className="m-close" data-bs-dismiss="modal" aria-label="Close">
                  <img src={closeIcon} alt="Close" />
                </button>
              </div>

              <div className="modal-inner">
                <div className="adddriver-form">
                  <div className="row">
                    {/* First Name */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">First Name<span> *</span></label>
                        <input type="text" name="first_name" maxLength={FIELD_MAX_LENGTHS.firstName} className={`common-input ${errors.first_name ? "error" : ""}`} value={formData.first_name} onChange={handleChange} />
                        <span className={`error-text ${errors.first_name ? "active" : ""}`}>{errors.first_name}</span>
                      </div>
                    </div>

                    {/* Last Name */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Last Name<span> *</span></label>
                        <input type="text" name="last_name" maxLength={FIELD_MAX_LENGTHS.lastName} className={`common-input ${errors.last_name ? "error" : ""}`} value={formData.last_name} onChange={handleChange} />
                        <span className={`error-text ${errors.last_name ? "active" : ""}`}>{errors.last_name}</span>
                      </div>
                    </div>

                    {/* Contact */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Contact No.<span> *</span></label>
                        <input type="tel" name="contact_no" maxLength={FIELD_MAX_LENGTHS.contact} className={`common-input ${errors.contact_no ? "error" : ""}`} value={formData.contact_no} onChange={handleChange} />
                        <span className={`error-text ${errors.contact_no ? "active" : ""}`}>{errors.contact_no}</span>
                      </div>
                    </div>

                    {/* Role */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Role<span> *</span></label>
                        <select name="role_id" className={`common-select ${errors.role_id ? "error" : ""}`} value={formData.role_id} onChange={handleChange}>
                          <option value="">--Select--</option>
                          {roles.map(role => (
                            <option key={role.id} value={role.id}>{role.role_name}</option>
                          ))}
                        </select>
                        <span className={`error-text ${errors.role_id ? "active" : ""}`}>{errors.role_id}</span>
                      </div>
                    </div>

                    {/* License No */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">License No.<span> *</span></label>
                        <input type="text" name="license_no" className={`common-input ${errors.license_no ? "error" : ""}`} value={formData.license_no} onChange={handleChange} />
                        <span className={`error-text ${errors.license_no ? "active" : ""}`}>{errors.license_no}</span>
                      </div>
                    </div>

                    {/* License Expiry */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">License Expiry<span> *</span></label>
                        <div className="custom-datetime-wrapper">
                          <input type="date" name="license_expiry" id="license-expiry-datetime" className={`custom-datetime-input common-input ${errors.license_expiry ? "error" : ""}`} value={formData.license_expiry} onChange={handleChange} />
                          <img src={calendarIcon} alt="Calendar" className="calendar-icon" onClick={() => document.getElementById("license-expiry-datetime")?.showPicker()} style={{ cursor: "pointer" }} />
                        </div>
                        <span className={`error-text ${errors.license_expiry ? "active" : ""}`}>{errors.license_expiry}</span>
                      </div>
                    </div>

                    {/* License Document */}
                    <div className="col-md-12">
                      <div className="cp-split">
                        <label className="fi-label">License Document<span> *</span></label>
                        <div className="hsfile-upload">
                          <div className="file-display">
                            <label className="file-upload-label dr-label">
                              {formData.license_file?.name || ""}
                            </label>
                            {formData.license_file && (
                              <img
                                src={redIcon}
                                alt="Remove"
                                className="remove-icon"
                                onClick={() => {
                                  setFormData((prev) => ({ ...prev, license_file: null }));
                                  if (fileInputRef.current) fileInputRef.current.value = "";
                                }}
                              />
                            )}
                          </div>
                          <label htmlFor="license_file_add" className="browse-btn">Browse</label>
                          <input
                            key={formData.license_file ? formData.license_file.name : "empty_add"}
                            type="file"
                            id="license_file_add"
                            name="license_file"
                            accept=".pdf"
                            ref={fileInputRef}
                            onChange={handleChange}
                          />
                        </div>
                        <span className={`error-text ${errors.license_file ? "active" : ""}`}>
                          {errors.license_file}
                        </span>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Email<span> *</span></label>
                        <input type="email" name="email_id" maxLength={FIELD_MAX_LENGTHS.email} className={`common-input ${errors.email_id ? "error" : ""}`} value={formData.email_id} onChange={handleChange} />
                        <span className={`error-text ${errors.email_id ? "active" : ""}`}>{errors.email_id}</span>
                      </div>
                    </div>

                    {/* Password */}
                    <div className="col-md-6">
                      <div
                        className={`cp-split driverpass-box ${isPasswordFocused ? "focused" : ""}`}
                        ref={passwordRef}
                      >
                        <label className="fi-label">
                          Password<span> *</span>
                        </label>
                        <div
                          className={`password-box dp-box ${isPasswordFocused ? "focused" : ""
                            }`}
                        >
                          {isPasswordFocused && formData.password === "" && (

                            <PasswordTooltip />
                          )}

                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            maxLength={FIELD_MAX_LENGTHS.password}
                            className={`common-input ${errors.password ? "error" : ""}`}
                            value={formData.password}
                            onFocus={() => setIsPasswordFocused(true)}
                            onChange={handleChange}
                          />
                          <i
                            className={`fa field-icon ${showPassword ? "fa-eye" : "fa-eye-slash"
                              }`}
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: "absolute",
                              top: "50%",
                              right: "10px",
                              transform: "translateY(-50%)",
                              cursor: "pointer",
                            }}
                          />
                        </div>

                        {/* Generate Password Button */}
                        <button
                          type="button"
                          className="generate-password"
                          onClick={() => {
                            const newPwd = autoGeneratePassword();
                            setFormData((prev) => ({ ...prev, password: newPwd }));
                            setShowPassword(true);
                          }}
                        >
                          <img src={generatePwdIcon} alt="Generate" />
                        </button>

                        <span
                          className={`error-text ${errors.password ? "active" : ""}`}
                        >
                          {errors.password}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="m-button">
                    <button type="submit" className="cp-button save-btn" disabled={loading}>
                      {loading ? <Loader /> : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddDriversModal;
